import React from 'react'

import CampaignContentQuestionAction from '@/app/module/campaigns/component/item/steps/content/actions-only'
import CampaignContentQuestionAPI from '@/app/module/campaigns/component/item/steps/content/api-actions'
import CampaignContentQuestionVoice from '@/app/module/campaigns/component/item/steps/content/question-voice'
import QuotaActions from '@/app/module/campaigns/component/item/steps/content/quota-actions'
import ResponseCati from '@/app/module/campaigns/component/item/steps/content/response-cati'
import ResponseSms from '@/app/module/campaigns/component/item/steps/content/response-sms'
import ResponseWhatsapp from '@/app/module/campaigns/component/item/steps/content/response-whatsapp'
import ResponseWhatsappFlow from '@/app/module/campaigns/component/item/steps/content/response-whatsapp-flow'
import CampaignContentQuestionTopup from '@/app/module/campaigns/component/item/steps/content/topup-actions'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import {
  CampaignFileResult,
  CampaignTemplateType,
  ImageProps,
  Language,
  LegacyCampaignItem,
  PartFamily,
  ReminderMessage,
  SenderIdOption,
  Message as TMessage,
  VoiceProps,
} from '@/app/module/campaigns/types'
import { WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import {
  CountryTimezone,
  FilesType,
  FileUploadType,
  PersonalizationType,
  TimezoneWithCountryCodeType,
} from '@/app/types'

type BaseProps = {
  apiPersonalizationList: PersonalizationType
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: {
    data: FilesType
    loadingItems: string[]
  }
  id: string
  index: number
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  total: number
  voiceList: BaseLanguageType[]
  voicePlayingId?: string
  whatsappSenderIds: SenderIdOption[]
  whatsappTab: WhatsAppTab

  getFile: (id: string, file: string) => Promise<CampaignFileResult>
  onChange: (item: Partial<TMessage>, changed?: boolean) => void
  onMicAccessError: () => void
  saveFile: (id: string, file: FileUploadType) => Promise<CampaignFileResult>
  setLoading: (loading: boolean) => void
  setPlayingId: (playId?: string) => void
}

type CampaignProps = BaseProps & {
  campaignType: LegacyCampaignItem['type']
  message: TMessage
}

type ReminderProps = BaseProps & {
  campaignType: CampaignTemplateType.Reminder
  message: ReminderMessage
}

type Props = CampaignProps | ReminderProps

const MessageResponse: React.FC<Props> = ({
  campaignType,
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  id,
  index,
  languages,
  message,
  orgId,
  personalizationList,
  senderIds,
  timezone,
  timezones,
  token,
  voiceList,
  voicePlayingId,
  whatsappSenderIds,
  whatsappTab,
  getFile,
  onChange,
  onMicAccessError,
  saveFile,
  setPlayingId,
}) => {
  const handleGetFile = React.useCallback((file: string) => getFile(id, file), [id, getFile])
  const handleSaveFile = React.useCallback((file: FileUploadType) => saveFile(id, file), [id, saveFile])

  const getImageProps = React.useCallback(
    (qid: string): ImageProps => ({
      id: qid,
      loading: files.loadingItems.indexOf(qid) > -1,
      getFile: handleGetFile,
      saveFileHandler: handleSaveFile,
    }),
    [files.loadingItems, handleGetFile, handleSaveFile],
  )
  const getVoiceProps = React.useCallback(
    (qid: string): VoiceProps => ({
      id: qid,
      files: files.data || {},
      filterType: campaignType,
      loading: files.loadingItems.indexOf(qid) > -1,
      personalizationList,
      playId: voicePlayingId,
      voiceList,
      getFile: handleGetFile,
      saveFileHandler: handleSaveFile,
      setPlaying: setPlayingId,
      onMicAccessError,
    }),
    [
      campaignType,
      files.data,
      files.loadingItems,
      personalizationList,
      voiceList,
      voicePlayingId,
      handleGetFile,
      handleSaveFile,
      onMicAccessError,
      setPlayingId,
    ],
  )

  const commonProps = React.useMemo(
    () => ({
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files: files.data || emptyObject,
      id,
      languages,
      index,
      orgId,
      personalizationList,
      senderIds,
      timezone,
      timezones,
      token,
      type: campaignType,
      whatsappSenderIds,
      changeHandler: onChange,
      getImageProps,
      getVoiceProps,
    }),
    [
      campaignType,
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files.data,
      id,
      index,
      languages,
      orgId,
      personalizationList,
      senderIds,
      timezone,
      timezones,
      token,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
      onChange,
    ],
  )

  if (!message.hasQuestion) {
    return null
  }

  switch (message.type) {
    case PartFamily.API:
      return <CampaignContentQuestionAPI {...commonProps} item={message} />

    case PartFamily.Action:
      return (
        <CampaignContentQuestionAction
          {...commonProps}
          item={message}
          title={
            'An “Actions only” message allows you to perform actions without having to send a SMS or making a call'
          }
        />
      )
    case PartFamily.QuotaActions:
      return <QuotaActions {...commonProps} item={message} />

    case PartFamily.CATI:
      return <ResponseCati {...commonProps} item={message} />

    case PartFamily.SMS:
      return <ResponseSms {...commonProps} item={message} useAi={message.useAi} />

    case PartFamily.Topup:
      return <CampaignContentQuestionTopup {...commonProps} item={message} />

    case PartFamily.Voice:
      return <CampaignContentQuestionVoice {...commonProps} item={message} />

    case PartFamily.WhatsApp:
      return (
        <ResponseWhatsapp
          {...commonProps}
          item={message}
          tab={whatsappTab}
          transcribe={message.transcribe}
          useAi={message.useAi}
        />
      )

    case PartFamily.WhatsAppFlow:
      return <ResponseWhatsappFlow {...commonProps} item={message} />

    default:
      return null
  }
}

const emptyObject = {}

export default React.memo(MessageResponse)
